import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { promoteFirstSuperAdmin } from '../admin/promoteFirstAdmin.ts';
import { X, ShieldAlert, CheckCircle, AlertCircle } from 'lucide-react';

const PromoteFirstAdmin: React.FC = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) return;
    
    try {
      setIsSubmitting(true);
      const result = await promoteFirstSuperAdmin(email);
      setResult(result);
      
      if (result.success) {
        // Attendre 3 secondes puis rediriger vers la page d'accueil
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : 'Une erreur est survenue'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
      <div className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 rounded-xl overflow-hidden max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-center mb-6">
            <ShieldAlert className="w-12 h-12 text-primary-end" />
          </div>
          
          <h1 className="text-2xl font-bold text-center text-white mb-2">
            Configuration administrateur
          </h1>
          
          <p className="text-gray-400 text-center mb-6">
            Cet écran vous permet de définir le premier super administrateur de l'application.
            Cette opération ne peut être effectuée qu'une seule fois.
          </p>
          
          {result ? (
            <div className={`mb-6 p-4 rounded-lg ${
              result.success ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
            }`}>
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 mt-0.5" />
                )}
                <div>
                  <p className="font-medium">
                    {result.success ? 'Succès' : 'Erreur'}
                  </p>
                  <p className="text-sm opacity-90">
                    {result.message}
                  </p>
                  {result.success && (
                    <p className="text-sm mt-2">
                      Vous allez être redirigé dans quelques secondes...
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1">
                  Email de l'administrateur
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-end text-white"
                  placeholder="admin@example.com"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !email}
                className="w-full bg-gradient-to-r from-primary-start to-primary-end text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  'Configurer l\'administrateur'
                )}
              </button>
            </form>
          )}
          
          {!result?.success && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Retour à l'accueil
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoteFirstAdmin; 